"use client"

import styles from "./styles/CleverFeedLocationSearchBar.module.scss";
import React, {useEffect, useMemo, useRef} from "react";
import ClickableLink from "../../utilities/ClickableLink";
import {useMainUrl} from "../../../hooks/useMainUrl";
import {useStores} from "../../../stores/useStores";
import UserDropDownMenu from "../UserDropDownMenu";
import {ReactTagify} from "../../grids/geo/ReactTagify";
import {ITag, TagifyRefObject} from "../../modals/Messaging/MessagingListRuleQueryBuilder";
import {getQueries} from "../../../utils/SchoolBlocksUtilities";
import SafeRouter from "../../utilities/SafeRouter";
import {observer} from "mobx-react";
import FontAwesome from "../../utilities/FontAwesome";
import classNames from "classnames";

const CleverFeedLocationSearchBar = observer(() => {
    const {organizationStore, modalStore, interfaceStore} = useStores();
    const tagifyRef = useRef<TagifyRefObject>(null);

    const urlQuery = useMemo(() => {
        return getQueries(interfaceStore.currentFullUrl);
    },[interfaceStore.currentFullUrl])

    const handleTagifyRemove = () => {
        SafeRouter.setQueries(["lat", undefined], ["long", undefined], ["radius", undefined], ["display", undefined]);
    }

    const addOrReplaceTag = () => {
        if (tagifyRef?.current?.tagify) {
            const allTags = tagifyRef?.current?.tagify.getTagElms();

            if (urlQuery.display) {
                const tag: ITag = {
                    value: urlQuery.display,
                }
                if (allTags.length > 0) {
                    tagifyRef.current.tagify.replaceTag(allTags[0], tag)
                } else {
                    tagifyRef.current.tagify.addTags([tag])
                }
            }
        }
    }

    useEffect(() => {
        addOrReplaceTag();
    }, [urlQuery.display])

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (tagifyRef?.current?.tagify?.DOM && !tagifyRef?.current?.tagify?.DOM.scope.contains(target.closest('.tagify__tag'))) {
            // Clicked empty space
            modalStore.addModal({ type: "locationSelection" });
        }
    };

    const locationSearchClassName = classNames({
        [styles.locationSearch]: true,
        [styles.locationSearchDisplay]: urlQuery.display,
    })

    return (
        <div className={styles.container}>
            <div className={styles.searchBar}>
                <div className={styles.titleAndLocationSearch}>
                    {!urlQuery.display && <ClickableLink className={styles.title} aria-label={"Go to Home Page"}
                                                         title={"Go to Home Page"}
                                                         href={useMainUrl()}>
                        <h1>{organizationStore.organization.title}</h1>
                    </ClickableLink>}
                    <div className={locationSearchClassName} onClick={handleClick}>
                        <span style={{color: "var(--blockBgTextColorNoBlocksUseSecondary)"}}>{!urlQuery.display &&
                            <FontAwesome name={'fa-location-dot'} prefix={'fas'}/>}</span>
                        <ReactTagify
                            ref={tagifyRef}
                            onKeydown={(e) => e.preventDefault()}
                            onReady={() => addOrReplaceTag()}
                            onRemove={(e) => handleTagifyRemove()}
                            placeholder={!urlQuery.display ? "Choose Location" : "Change Location"}
                            className={styles.tagify}
                            settings={{
                                userInput: false,
                                editTags: false,
                                backspace: false,
                                addTagOnBlur: false,
                                pasteAsTags: false,
                                templates: {
                                    tag(tagData, {settings: _s}) {
                                        return `<tag title="${(tagData.title || tagData.value)}"
                                                contenteditable='false'
                                                spellcheck='false'
                                                tabIndex="${_s.a11y.focusableTags ? 0 : -1}"
                                                class="${_s.classNames.tag} ${tagData.class || ""}"
                                                ${this.getAttributes(tagData)}>
                                                <x title='' class="${_s.classNames.tagX}" role='button' aria-label='remove tag'></x>
                                                    <div>
                                                        <div style="margin-right: 10px;">
                                                            <span style="color: var(--blockBgTextColorNoBlocksUseSecondary)" class="fa-location-dot fa-solid"/>                                                          
                                                        </div> 
                                                        <span class="${_s.classNames.tagText}">
                                                                <span>${tagData.value}</span>
                                                        </span>                                        
                                                    </div>
                                                </tag>`
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
                <div className={styles.buttons}>
                    <div>
                        <button onClick={() =>
                            modalStore.addModal({type: "languageSelection"})}
                                className={"sb-navbar-button-text"}
                                aria-label={"Change Language"}
                                title={"Change Language"}
                        >
                            <span
                                className="fas  fa-language" role="presentation"
                                aria-hidden="true"
                            />
                        </button>
                        <ClickableLink
                            id={'search-cell'}
                            className={"sb-navbar-button-text"}
                            href={"/search"}
                            rel="noopener noreferrer"
                            aria-label="Link to Search Page"
                            title="Link to Search Page"
                        >
                        <span
                            className="fas  fa-search sb-navbar-search-icon" role="presentation"
                            aria-hidden="true"
                        />
                        </ClickableLink>
                    </div>
                    <UserDropDownMenu menuClassName={styles.loginDropDownMenu} buttonClassName={styles.loginButton}/>
                </div>
            </div>
        </div>

    );
});

export default CleverFeedLocationSearchBar;
